import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X, ArrowUpRight } from "lucide-react";
import api from "../../lib/api";

export default function LiveSearch({ onClose, onNavigate }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } }).then((r) => {
        setResults(r.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const Section = ({ title, items, renderItem }) => items.length > 0 && (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-gold/80 mb-1 px-1">{title}</div>
      <div className="space-y-1">{items.map(renderItem)}</div>
    </div>
  );

  const any = results && Object.values(results).some((arr) => arr.length > 0);

  return (
    <div className="border-t border-white/10 bg-navy" data-testid="header-search-panel">
      <div className="container-site py-5">
        <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) onNavigate(`/kategoriler?q=${encodeURIComponent(q.trim())}`); }} className="flex items-center gap-2">
          <Search size={18} className="text-white/50" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent outline-none text-white placeholder-white/40 py-2"
            data-testid="header-search-input"
          />
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white p-1" aria-label="Kapat">
            <X size={16} />
          </button>
        </form>

        {q.trim().length >= 2 && (
          <div className="mt-4 bg-white rounded-md p-4 max-h-[60vh] overflow-y-auto admin-scroll" data-testid="live-search-results">
            {loading && <p className="text-xs text-gray-500">{t("common.loading")}</p>}
            {!loading && results && !any && <p className="text-sm text-gray-500">{t("search.no_results")}</p>}
            {!loading && results && (
              <>
                <Section title={t("search.sections.products")} items={results.products} renderItem={(p) => (
                  <button key={p.id} onClick={() => onNavigate(`/urun/${p.slug}`)} className="w-full flex items-center gap-3 p-2 hover:bg-softbg rounded text-left" data-testid={`search-product-${p.slug}`}>
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="h-10 w-10 rounded object-cover" />}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-navy">{p.name}</div>
                      <div className="text-[11px] text-gray-500 line-clamp-1">{p.short_description}</div>
                    </div>
                    <ArrowUpRight size={14} className="text-gray-400" />
                  </button>
                )} />
                <Section title={t("search.sections.categories")} items={results.categories} renderItem={(c) => (
                  <button key={c.slug} onClick={() => onNavigate(`/kategori/${c.slug}`)} className="w-full flex items-center gap-3 p-2 hover:bg-softbg rounded text-left" data-testid={`search-cat-${c.slug}`}>
                    {c.image && <img src={c.image} alt="" className="h-8 w-8 rounded object-cover" />}
                    <span className="text-sm font-medium text-navy flex-1">{c.name}</span>
                    <ArrowUpRight size={14} className="text-gray-400" />
                  </button>
                )} />
                <Section title={t("search.sections.guide")} items={results.guide} renderItem={(g) => (
                  <button key={g.slug} onClick={() => onNavigate(`/acilis-rehberi/${g.slug}`)} className="w-full p-2 hover:bg-softbg rounded text-left" data-testid={`search-guide-${g.slug}`}>
                    <div className="text-sm font-medium text-navy">{g.title_tr}</div>
                    <div className="text-[11px] text-gray-500 line-clamp-1">{g.summary}</div>
                  </button>
                )} />
                <Section title={t("search.sections.rooms")} items={results.rooms} renderItem={(r) => (
                  <button key={r.slug} onClick={() => onNavigate(`/oda-tipleri/${r.slug}`)} className="w-full flex items-center gap-3 p-2 hover:bg-softbg rounded text-left" data-testid={`search-room-${r.slug}`}>
                    {r.image && <img src={r.image} alt="" className="h-8 w-8 rounded object-cover" />}
                    <span className="text-sm font-medium text-navy">{r.name_tr}</span>
                  </button>
                )} />
                <Section title={t("search.sections.blog")} items={results.blog} renderItem={(b) => (
                  <button key={b.slug} onClick={() => onNavigate(`/blog/${b.slug}`)} className="w-full flex items-center gap-3 p-2 hover:bg-softbg rounded text-left" data-testid={`search-blog-${b.slug}`}>
                    {b.cover_image && <img src={b.cover_image} alt="" className="h-8 w-8 rounded object-cover" />}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-navy">{b.title_tr}</div>
                      <div className="text-[11px] text-gray-500 line-clamp-1">{b.excerpt}</div>
                    </div>
                  </button>
                )} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
